// Add a device — the claim-code dialog.
//
// Two states in one modal, because they are two halves of one action and splitting
// them would let an admin close the first and lose the result of the second:
//
//     FORM    name the device -> "Generate claim code"
//     CODE    the code, once, with its expiry counting down
//
// ─── The code is shown exactly once ────────────────────────────────────
// The server stores only a SHA-256 hash of it. There is no endpoint that returns it
// again, because there is nothing to return from — a lost code is replaced by
// generating another, not by looking it up. This dialog has to say so plainly, or an
// admin will close it expecting to find the code in the pending list later.
//
// It is deliberately not written to localStorage, not put in the page title, and not
// logged. It lives in component state until the dialog unmounts.
import { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Form, Input, Modal, Space, Statistic, Typography } from 'antd';
import { CheckOutlined, CopyOutlined, ReloadOutlined } from '@ant-design/icons';

import { devices as fleet } from '../api/client';
import { useAuthStore } from '../state/authStore';
import { BRAND } from '../theme';

const { Text, Paragraph } = Typography;

interface Props {
    open: boolean;
    onClose: () => void;
    /** Called after a claim is created so Devices can refresh its pending list. */
    onCreated: () => void;
}

export default function ProvisionDeviceDialog({ open, onClose, onCreated }: Props) {
    const session = useAuthStore((s) => s.session);
    // From the App context, so a toast is drawn with this app's theme rather
    // than Ant Design's defaults.
    const { message } = App.useApp();

    const [label, setLabel] = useState('');
    const [location, setLocation] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [code, setCode] = useState<string | null>(null);
    const [expiresAt, setExpiresAt] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    // Reset on every open. Without this, reopening would show the PREVIOUS device's
    // code — confusing, and a small leak of a live credential.
    useEffect(() => {
        if (!open) return;
        setLabel('');
        setLocation('');
        setCode(null);
        setExpiresAt(null);
        setCopied(false);
    }, [open]);

    const valid = label.trim().length > 0;

    const generate = useCallback(async () => {
        if (!valid || !session) return;
        setSubmitting(true);
        try {
            const r = await fleet.provision(session, label.trim(), location.trim() || null);
            setCode(r.code);
            setExpiresAt(r.claim.expiresAt);
            onCreated();
        } catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not generate a code.');
        } finally {
            setSubmitting(false);
        }
    }, [label, location, valid, session, onCreated]);

    async function copy() {
        if (!code) return;
        try {
            await navigator.clipboard.writeText(code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard access is refused in plenty of ordinary situations. The code
            // is selectable, so say that rather than leaving a dead button.
            message.warning('Could not copy — select the code and copy it manually.');
        }
    }

    return (
        <Modal
            title={code ? 'Device claim code' : 'Add device'}
            open={open}
            onCancel={onClose}
            destroyOnHidden
            // Once the code is on screen, a stray click on the mask would take it away
            // for good. Closing has to be deliberate from that point.
            maskClosable={!code}
            footer={
                code
                    ? [
                          <Button key="done" type="primary" onClick={onClose}>
                              Done
                          </Button>,
                      ]
                    : [
                          <Button key="cancel" onClick={onClose}>
                              Cancel
                          </Button>,
                          <Button
                              key="gen"
                              type="primary"
                              loading={submitting}
                              disabled={!valid}
                              onClick={() => void generate()}
                          >
                              Generate claim code
                          </Button>,
                      ]
            }
        >
            {code === null ? (
                <>
                    <Paragraph type="secondary" style={{ fontSize: 13 }}>
                        Name the bridge first. You will get a one-time code to enter on the
                        device while commissioning it.
                    </Paragraph>
                    <Form layout="vertical">
                        <Form.Item
                            label="Device name"
                            help="What it is called on the dashboard, e.g. Gate 1, North Weighbridge"
                            required
                        >
                            <Input
                                value={label}
                                onChange={(e) => setLabel(e.target.value)}
                                maxLength={64}
                                autoFocus
                                onPressEnter={() => void generate()}
                            />
                        </Form.Item>
                        <Form.Item label="Location">
                            <Input
                                value={location}
                                onChange={(e) => setLocation(e.target.value)}
                                maxLength={120}
                            />
                        </Form.Item>
                    </Form>
                </>
            ) : (
                <>
                    <span className="sb-section-label">Device name</span>
                    <div style={{ marginBottom: 16, marginTop: 4 }}>
                        <Text strong style={{ fontSize: 15 }}>
                            {label.trim()}
                        </Text>
                    </div>

                    <span className="sb-section-label">Claim code</span>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            background: '#f8fafc',
                            border: `1px dashed ${BRAND.primary}`,
                            borderRadius: 8,
                            padding: '14px 16px',
                            marginTop: 4,
                        }}
                    >
                        <Text
                            code
                            style={{
                                fontSize: 22,
                                letterSpacing: 2,
                                fontWeight: 600,
                                flex: 1,
                                background: 'none',
                                // So a double-click takes the whole code, not one group.
                                userSelect: 'all',
                            }}
                        >
                            {code}
                        </Text>
                        <Button
                            icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                            onClick={() => void copy()}
                            type={copied ? 'default' : 'primary'}
                        >
                            {copied ? 'Copied' : 'Copy'}
                        </Button>
                    </div>

                    {expiresAt && (
                        <div style={{ marginTop: 14 }}>
                            <Statistic.Countdown
                                title={
                                    <span style={{ fontSize: 12, color: BRAND.muted }}>Expires in</span>
                                }
                                value={new Date(expiresAt).getTime()}
                                format="mm:ss"
                                valueStyle={{ fontSize: 18, color: BRAND.primary, fontWeight: 600 }}
                            />
                        </div>
                    )}

                    <Alert
                        style={{ marginTop: 16 }}
                        type="warning"
                        showIcon
                        message="Shown once — it is not stored"
                        description={
                            <div style={{ fontSize: 13 }}>
                                Enter this code <strong>only on the bridge you are commissioning</strong>.
                                Anyone who has it can register a device into this account until it
                                expires or is used.
                                <div style={{ marginTop: 8, color: BRAND.muted }}>
                                    Lost it? Cancel the pending device and add it again —{' '}
                                    <ReloadOutlined /> a new code is generated each time.
                                </div>
                            </div>
                        }
                    />

                    <Space direction="vertical" size={2} style={{ marginTop: 14 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            The device shows as <strong>Pending</strong> until the bridge claims it.
                        </Text>
                    </Space>
                </>
            )}
        </Modal>
    );
}
